import {
  useIsMobile,
  useModalContext,
  WSButton,
  WSContextMenu,
  WSDivider,
  WSFilters,
  WSFlexBox,
  WSIcon,
  WSPage,
  wsSearch,
  WSText
} from "@wingspanhq/fe-component-library";
import React, { useState } from "react";
import { useHistory } from "react-router-dom";
import { WSPersistentUpgradeButton } from "../../../components/Membership/WSPersistentUpgradeButton";
import { useCollaboratorsQuery } from "../../../query/payments/queries";
import {
  filterDuplicateCollaborators,
  getVisibleCollaborators
} from "../../../query/payments/selectors";
import { WSQueries } from "../../../query/WSQuery";
import { WSFrontendFeature } from "../../../Settings/utils/subscriptionUtils";
import {
  ADD_COLLABORATOR_MODAL,
  AddCollaboratorModal
} from "../../components/AddCollaboratorModal/AddCollaboratorModal";
import { CollaboratorsTable } from "../../components/CollaboratorsTable/CollaboratorsTable";
import styles from "./InvoicesCollaborators.module.scss";
import { useFeatureFlags } from "../../../query/hooks/useFeatureFlags";
import { AddCollaboratorToGroupModal } from "./AddCollaboratorToGroupModal";
import { sortKind } from "fast-fuzzy";

export const InvoicesCollaborators: React.FC = () => {
  const history = useHistory();
  const collaboratorsQuery = useCollaboratorsQuery();
  const featureFlagsQuery = useFeatureFlags();
  const { openModal } = useModalContext();
  const [searchText, setSearchText] = useState("");
  const isMobile = useIsMobile();

  return (
    <>
      <AddCollaboratorToGroupModal />
      <AddCollaboratorModal />
      <WSPage
        title="Collaborators"
        breadcrumb={
          featureFlagsQuery.data?.collaboratorGroupsUI
            ? undefined
            : {
                label: "Contacts",
                icon: "back",
                onClick: () => {
                  history.push("/member/invoices/contacts");
                }
              }
        }
        sideContent={
          <WSContextMenu
            isSameWidthAsTrigger
            className={styles.buttonActionsWrapper}
            name="actions"
            title="Add Collaborator"
            renderMenuTriggerView={
              <WSFlexBox
                ml="S"
                mb="XS"
                className={styles.buttonActions}
                alignItems="stretch"
              >
                <WSText.Heading4
                  align="center"
                  color="white"
                  className={styles.buttonActionsText}
                  py="S"
                  px="M"
                  onClick={() => {
                    openModal(ADD_COLLABORATOR_MODAL);
                  }}
                >
                  Add collaborator
                </WSText.Heading4>
                <WSFlexBox.Center px="S">
                  <WSIcon
                    name="dropDown"
                    color="white"
                    size="S"
                    className={styles.buttonActionsIcon}
                  />
                </WSFlexBox.Center>
              </WSFlexBox>
            }
            defaultPlacement="bottom-start"
            menuItems={[
              {
                label: "Bulk upload collaborators",
                onClick() {
                  history.push(
                    "/member/invoices/contacts/collaborators/bulk-create/info"
                  );
                }
              }
            ]}
          />
        }
      >
        <WSQueries queries={{ collaboratorsQuery, featureFlagsQuery }}>
          {({
            collaboratorsQuery,
            featureFlagsQuery: { data: featureFlags }
          }) => {
            let collaborators = getVisibleCollaborators(
              collaboratorsQuery.data
            );
            if (featureFlags.deduplicateCollaboratorList) {
              collaborators = filterDuplicateCollaborators(collaborators);
            }

            if (searchText) {
              collaborators = wsSearch(searchText, collaborators, {
                normalizeWhitespace: true,
                ignoreCase: true,
                keySelector: collaborator =>
                  [
                    collaborator.member.user.profile?.firstName,
                    collaborator.member.user.profile?.lastName,
                    collaborator.member.user.email,
                    collaborator.member.profile?.company?.name,
                    collaborator.collaboratorId,
                    collaborator.labels.contractorId
                  ]
                    .filter(Boolean)
                    .join(" ")
                    .trim(),
                threshold: 0.7,
                sortBy: sortKind.bestMatch
              });
            }

            return (
              <>
                {isMobile && <WSDivider fullScreen my="M" />}

                <WSFilters
                  onSearch={newSearchText => {
                    setSearchText(newSearchText.toLowerCase());
                  }}
                  searchQuery={searchText}
                  mb={isMobile ? "S" : "L"}
                />

                {collaborators.length > 0 ? (
                  <CollaboratorsTable
                    collaborators={collaborators}
                    listPath="/member/invoices/contacts/collaborators"
                  />
                ) : searchText ? (
                  <WSText>
                    No results found.{" "}
                    <WSButton.Link
                      inline
                      onClick={() => {
                        setSearchText("");
                      }}
                    >
                      Clear search
                    </WSButton.Link>
                  </WSText>
                ) : (
                  <WSText>
                    No collaborators yet.{" "}
                    <WSPersistentUpgradeButton
                      feature={WSFrontendFeature.InviteAndPayCollaborator}
                      kind="Link"
                      inline
                      onClick={() => {
                        openModal(ADD_COLLABORATOR_MODAL);
                      }}
                    >
                      Add a collaborators
                    </WSPersistentUpgradeButton>
                  </WSText>
                )}
              </>
            );
          }}
        </WSQueries>
      </WSPage>
    </>
  );
};
